import PropTypes from 'prop-types';
import WaterfallChart from '@/components/charts/WaterfallChart';
import { parseTEAForWaterfall, parseLCAForWaterfall } from './waterfallUtils';

/**
 * Multi-pathway comparison result card.
 *
 * compact=true  → chat: summary table
 * compact=false → workspace: summary table + expandable per-pathway detail
 */

function RankBar({ value, maxValue }) {
  const pct = maxValue > 0 ? (value / maxValue) * 100 : 0;
  return (
    <span className="ml-2 w-12 h-1 bg-stone-100 rounded-sm overflow-hidden inline-block align-middle">
      <span className="block h-full bg-slate-400 rounded-sm" style={{ width: `${pct}%` }} />
    </span>
  );
}

RankBar.propTypes = {
  value: PropTypes.number.isRequired,
  maxValue: PropTypes.number.isRequired,
};

function ScopeGrid({ c }) {
  return (
    <div className="grid grid-cols-3 gap-3">
      {[
        { label: 'Scope 1', value: c.lca_scope1 },
        { label: 'Scope 2', value: c.lca_scope2 },
        { label: 'Scope 3', value: c.lca_scope3 },
      ].map(({ label, value }) => (
        <div key={label}>
          <div className="text-[10px] text-slate-400 uppercase tracking-wide mb-0.5">{label}</div>
          <div className="text-sm font-semibold text-slate-700 tabular-nums">
            {(value ?? 0).toFixed(2)}
          </div>
        </div>
      ))}
    </div>
  );
}

ScopeGrid.propTypes = {
  c: PropTypes.shape({
    lca_scope1: PropTypes.number,
    lca_scope2: PropTypes.number,
    lca_scope3: PropTypes.number,
  }).isRequired,
};

function PathwayDetail({ c }) {
  const pathwayName = c.name || c.pathway_name;
  const hasTEABreakdown = c.tea_breakdown?.length > 0;
  const hasLCABreakdown = c.lca_breakdown?.length > 0;

  return (
    <details className="border border-stone-100 rounded-md overflow-hidden group">
      <summary className="flex items-center justify-between px-3 py-2 cursor-pointer select-none bg-stone-50 hover:bg-stone-100 list-none transition-colors">
        <span className="text-sm font-medium text-slate-700">{pathwayName}</span>
        <div className="flex items-center gap-4 text-xs tabular-nums text-slate-500">
          {c.tea_total != null && (
            <span>
              <span className="font-semibold text-slate-700">{c.tea_total.toFixed(2)}</span>
              {' '}{c.tea_unit}
            </span>
          )}
          {c.lca_total != null && (
            <span>
              <span className="font-semibold text-slate-700">{c.lca_total.toFixed(2)}</span>
              {' '}{c.lca_unit}
            </span>
          )}
          <span className="text-slate-300 group-open:hidden">▸</span>
          <span className="text-slate-300 hidden group-open:inline">▾</span>
        </div>
      </summary>

      <div className="px-4 py-3 space-y-4">
        {c.lca_total != null && (
          <div>
            <div className="text-[10px] text-slate-400 uppercase tracking-wide mb-2">Carbon Intensity</div>
            <ScopeGrid c={c} />
            {hasLCABreakdown && (
              <div className="mt-3">
                <WaterfallChart
                  data={parseLCAForWaterfall(c.lca_breakdown)}
                  label="Carbon Intensity"
                  yAxisLabel={c.lca_unit ?? ''}
                />
              </div>
            )}
          </div>
        )}
        {hasTEABreakdown && (
          <div>
            <div className="text-[10px] text-slate-400 uppercase tracking-wide mb-2">Cost Breakdown</div>
            <WaterfallChart
              data={parseTEAForWaterfall(c.tea_breakdown)}
              label="Levelized Cost"
              yAxisLabel={c.tea_unit ?? ''}
            />
          </div>
        )}
      </div>
    </details>
  );
}

PathwayDetail.propTypes = {
  c: PropTypes.shape({
    name: PropTypes.string,
    pathway_name: PropTypes.string,
    lca_total: PropTypes.number,
    lca_unit: PropTypes.string,
    lca_scope1: PropTypes.number,
    lca_scope2: PropTypes.number,
    lca_scope3: PropTypes.number,
    lca_breakdown: PropTypes.array,
    tea_total: PropTypes.number,
    tea_unit: PropTypes.string,
    tea_breakdown: PropTypes.array,
  }).isRequired,
};

function ComparisonResultCard({ data, compact = true }) {
  const { name, cases } = data;

  if (!cases || cases.length === 0) return null;

  const validCases = cases.filter(c => c.tea_total != null || c.lca_total != null);
  if (validCases.length === 0) return null;

  const maxTea = Math.max(...validCases.filter(c => c.tea_total != null).map(c => c.tea_total), 0.001);
  const maxLca = Math.max(...validCases.filter(c => c.lca_total != null).map(c => c.lca_total), 0.001);
  const hasTea = validCases.some(c => c.tea_total != null);
  const hasLca = validCases.some(c => c.lca_total != null);
  const teaUnit = validCases.find(c => c.tea_unit)?.tea_unit ?? '';
  const lcaUnit = validCases.find(c => c.lca_unit)?.lca_unit ?? '';

  const card = (
    <div className={`bg-white border border-stone-200 rounded-md p-4 space-y-4 ${compact ? 'w-full max-w-[85%]' : 'w-full'}`}>
      {/* Header */}
      <div>
        <div className="text-[10px] text-slate-400 uppercase tracking-wide mb-0.5">Comparison</div>
        <div className="text-sm font-semibold text-slate-700">{name}</div>
      </div>

      {/* Summary table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="border-b border-stone-100">
              <th className="text-left py-1.5 px-1 font-medium text-slate-400 w-2/5">Pathway</th>
              {hasTea && (
                <th className="text-right py-1.5 px-1 font-medium text-slate-400">
                  Cost{teaUnit && <span className="font-normal ml-1 opacity-70">({teaUnit})</span>}
                </th>
              )}
              {hasLca && (
                <th className="text-right py-1.5 px-1 font-medium text-slate-400">
                  Emissions{lcaUnit && <span className="font-normal ml-1 opacity-70">({lcaUnit})</span>}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {validCases.map((c, i) => (
              <tr key={c.case_id ?? i} className="border-b border-stone-50 last:border-0">
                <td className="py-2 px-1 text-slate-700 font-medium">{c.name || c.pathway_name}</td>
                {hasTea && (
                  <td className="py-2 px-1 text-right tabular-nums text-slate-700">
                    {c.tea_total != null ? (
                      <>
                        <span className="font-semibold">{c.tea_total.toFixed(2)}</span>
                        <RankBar value={c.tea_total} maxValue={maxTea} />
                      </>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                )}
                {hasLca && (
                  <td className="py-2 px-1 text-right tabular-nums text-slate-700">
                    {c.lca_total != null ? (
                      <>
                        <span className="font-semibold">{c.lca_total.toFixed(2)}</span>
                        <RankBar value={c.lca_total} maxValue={maxLca} />
                      </>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Per-pathway detail — workspace only */}
      {!compact && (
        <div className="space-y-2 pt-1 border-t border-stone-100">
          <div className="text-[10px] text-slate-400 uppercase tracking-wide">Individual Pathways</div>
          {validCases.map((c, i) => (
            <PathwayDetail key={c.case_id ?? i} c={c} />
          ))}
        </div>
      )}
    </div>
  );

  if (!compact) return card;
  return (
    <div className="flex gap-2.5 items-start">
      <div className="w-6 shrink-0" />
      {card}
    </div>
  );
}

ComparisonResultCard.propTypes = {
  compact: PropTypes.bool,
  data: PropTypes.shape({
    comparison_id: PropTypes.string,
    name: PropTypes.string,
    cases: PropTypes.array,
  }).isRequired,
};

export default ComparisonResultCard;
